import { useParams } from 'react-router-dom'
import {
  BusinessUserType,
  useGetBusinessProfileQuery,
  useGetMyProfileQuery,
} from '__generated__/api-types-and-hooks'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { useMemo } from 'react'
import { getTenantId } from 'utils/getTenantId'
import Avatar from 'appAssets/appImages/Avatar.png'

export interface IAssigneeOptions {
  label: string
  value: string
  type: BusinessUserType
  email?: string
  user: {
    id: string
    firstName: string
    lastName: string
    email?: string
  }
  isAdvisor?: boolean
}
interface IUseGetBusinessProfileProps {
  paramUserId?: string
  enabled?: boolean
}

export const useGetBusinessProfile = ({ paramUserId, enabled }: IUseGetBusinessProfileProps) => {
  const tenantId = getTenantId()
  const { clientId } = useParams()
  const userId = useSelector((state: RootState) => state.user.user.id)
  const user = useSelector((state: RootState) => state.user.user)
  // Fetch business profile
  const { data: businessData } = useGetBusinessProfileQuery(
    { id: paramUserId ?? clientId ?? userId },
    { refetchOnWindowFocus: false, enabled: enabled ?? true }
  )
  // Fetch user profile
  const { data: userProfile } = useGetMyProfileQuery(
    { userId: paramUserId ?? clientId ?? userId, tenantId },
    { refetchOnWindowFocus: false, enabled: enabled ?? true }
  )
  const advisors = useMemo(() => businessData?.getBusinessProfile?.advisors, [businessData])

  const assigneeOptions: IAssigneeOptions[] = useMemo(() => {
    const userOptions: IAssigneeOptions[] = []

    // Add team members/users
    if (businessData?.getBusinessProfile?.users?.length) {
      businessData.getBusinessProfile.users.forEach((user) => {
        userOptions.push({
          value: user.userId,
          label: `${user.user.firstName} ${user.user.lastName}`,
          type: user.type,
          email: user.user.email || undefined,
          user: {
            firstName: user.user.firstName ?? '',
            lastName: user.user.lastName ?? '',
            id: user.user.id,
            email: user.user.email || undefined,
          },
          isAdvisor: false,
        })
      })
    }

    // Add advisors - always include them if they exist
    if (advisors?.length) {
      advisors.forEach((advisor) => {
        // Avoid duplicates - check if advisor is already in the list
        const alreadyExists = userOptions.some((opt) => opt.value === advisor.id)
        if (!alreadyExists) {
          userOptions.push({
            value: advisor.id,
            label: `${advisor.firstName} ${advisor.lastName}`,
            type: BusinessUserType.Member,
            email: advisor.email || undefined,
            user: {
              id: advisor.id,
              firstName: advisor.firstName ?? '',
              lastName: advisor.lastName ?? '',
              email: advisor.email || undefined,
            },
            isAdvisor: true,
          })
        }
      })
    }

    return userOptions
  }, [advisors, businessData?.getBusinessProfile?.users])

  // Compute userName from userProfile
  const userName = useMemo(() => {
    return `${userProfile?.getMyProfile.firstName || user.firstName} ${
      userProfile?.getMyProfile.lastName || user.lastName
    }`
  }, [userProfile, user])

  const userFirstName = useMemo(() => {
    return userProfile?.getMyProfile.firstName
  }, [userProfile])

  const userLastName = useMemo(() => {
    return userProfile?.getMyProfile.lastName
  }, [userProfile])

  // Compute advisorsData from userBoAdvisorsData and userName
  const advisorsData: Array<{ label: string; value: string }> = useMemo(() => {
    return [
      ...(advisors?.map((advisor) => ({
        value: advisor?.id,
        label: `${advisor?.firstName ?? ''} ${advisor?.lastName ?? ''}`,
      })) ?? []),
      {
        value: clientId ?? userId,
        label: userName,
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advisors, userName])

  // Get logo from businessData
  const logo = useMemo(
    () =>
      businessData?.getBusinessProfile?.logo?.url
        ? businessData?.getBusinessProfile?.logo?.url
        : businessData?.getBusinessProfile?.avatar ?? Avatar,
    [businessData]
  )

  const userAvatar = useMemo(() => businessData?.getBusinessProfile?.avatar, [businessData])

  // Get hours spent per day from businessData
  const hoursSpentPerDay: number = useMemo(
    () => Math.ceil((businessData?.getBusinessProfile?.hoursSpentPerWeek ?? 40) / 5),
    [businessData]
  )

  return {
    advisors,
    assigneeOptions,
    userName,
    userFirstName,
    userLastName,
    advisorsData,
    userAvatar,
    logo,
    hoursSpentPerDay,
    businessData,
  }
}
